/**
 * CMTR review: ON HOLD and REJECTED outcomes, the current result, and the review copy register.
 * CR-2026-CMTR-STAMP (2026-10-15). Server: /api/heat/:id/cmtr-review, /api/heat/:id/cmtr-review-copies[/:copyId/file].
 * ACCEPTED is the existing "Verify CMTR Compliance" action. Every decision files a stamped review copy on the server;
 * the original CMTR file is never changed. A rejection cites the NCR raised for this heat.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import ApiCall from "../../utils/apicall";
import { hasPermission } from "../../utils/hasPermission";

import SmartTable from "../SmartTable/SmartTable";
const msgOf = (e, f) => e?.response?.data?.message || e?.message || f;
const LABEL = { accepted: "ACCEPTED", rejected: "REJECTED", on_hold: "ON HOLD" };
const COLOR = { accepted: "success", rejected: "error", on_hold: "warning" };
const norm = (v) => String(v == null ? "" : v).trim().toUpperCase();
const utc = (d) => (d ? `${new Date(d).toISOString().slice(0, 16).replace("T", " ")} UTC` : "");

// The NCRs that can back a rejection: the web register's NCRs naming this heat (case and spaces ignored), not voided.
// The server applies the same rule (and the company).
export const ncrsForHeat = (list, heatNumber) => (Array.isArray(list) ? list : [])
  .filter((n) => norm(heatNumber) !== "" && norm(n.heat_number) === norm(heatNumber) && String(n.status || "").toLowerCase() !== "voided");

// Hold and Reject are offered to a CMTR reviewer, for a heat with a CMTR file that is not rejected, while the heat is
// quarantined (or barcode-verified). A CMTR already marked compliant may still be held or rejected before release:
// that withdraws the compliant mark (review P2). The server checks the same and more.
export const canDecideCmtr = (canReview, heat) => {
  const hs = String(heat?.status || "").toLowerCase();
  return !!canReview && !!heat?.mtcFilename && heat?.cmtr_review_status !== "rejected"
    && (hs === "quarantine" || hs === "verified");
};

// Blob errors carry the server's JSON message inside the blob.
async function blobMessage(e, fallback) {
  const d = e?.response?.data;
  if (d instanceof Blob) { try { return JSON.parse(await d.text()).message || fallback; } catch (_) { return fallback; } }
  return msgOf(e, fallback);
}

export default function CmtrReviewPanel({ heat, openedMtc, onHeatChanged }) {
  const { loginUser } = useSelector((s) => s.userSlice);
  const perms = loginUser?.decodedToken?.permissions || [];
  const canReview = hasPermission(perms, "review_cmtr");
  const heatId = heat?.id;
  const [copies, setCopies] = useState(null);
  const [msg, setMsg] = useState(null);
  const [dialog, setDialog] = useState(null); // "on_hold" | "rejected"
  const [reason, setReason] = useState("");
  const [ncrs, setNcrs] = useState([]);
  const [ncrId, setNcrId] = useState("");
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(null);

  const load = useCallback(async () => {
    if (!heatId) return;
    try { const r = await ApiCall.get(`/heat/${heatId}/cmtr-review-copies`); setCopies(r.data?.data?.copies || []); }
    catch (e) { setCopies(null); if (e?.response?.status !== 403) setMsg({ severity: "error", text: msgOf(e, "Could not load the CMTR review copies.") }); }
  }, [heatId]);
  // Re-read the register whenever the heat's CMTR or its review changes.
  useEffect(() => { load(); }, [load, heat?.mtcFilename, heat?.cmtr_review_status, heat?.cmtrVerified]);

  const st = heat?.cmtr_review_status || null;
  const canDecide = canDecideCmtr(canReview, heat);

  const open = async (outcome) => {
    setMsg(null);
    // Same rule as Verify: the reviewer must have opened the current CMTR file.
    if (!openedMtc || openedMtc !== heat.mtcFilename) { setMsg({ severity: "warning", text: "Open the current CMTR (View / Download CMTR) before reviewing it." }); return; }
    setReason(""); setNcrId(""); setDialog(outcome);
    if (outcome === "rejected") {
      try {
        const r = await ApiCall.get("/ncr");
        const list = Array.isArray(r.data?.data) ? r.data.data : [];
        setNcrs(ncrsForHeat(list, heat.heatNumber));
      } catch (e) { setNcrs([]); }
    }
  };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const body = { outcome: dialog, reason, mtcFilename: openedMtc };
      if (dialog === "rejected") Object.assign(body, { ncrId: Number(ncrId), ncrSource: "ncr_reports" });
      const r = await ApiCall.patch(`/heat/${heatId}/cmtr-review`, body);
      setDialog(null);
      setMsg({ severity: dialog === "rejected" ? "error" : "warning", text: `${r.data?.message || "Recorded."} A stamped review copy was filed below; the original CMTR is unchanged.` });
      if (onHeatChanged) await onHeatChanged();
      await load();
    } catch (e) {
      setMsg({ severity: "error", text: msgOf(e, "The review was not recorded.") });
    } finally { setBusy(false); }
  };

  const openCopy = async (c) => {
    if (opening) return;
    setOpening(c.id);
    try {
      const r = await ApiCall.get(`/heat/${heatId}/cmtr-review-copies/${c.id}/file`, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank");
      if (!w) { const a = document.createElement("a"); a.href = url; a.download = `CMTR-review-copy-${heat.heatNumber}-${c.number}.pdf`; document.body.appendChild(a); a.click(); a.remove(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setMsg({ severity: "error", text: await blobMessage(e, "Could not open the review copy.") });
    } finally { setOpening(null); }
  };

  const reasonOk = reason.trim().length >= 5 && reason.trim().length <= 1000;
  const current = (copies || []).find((c) => c.current);

  return (
    <Box sx={{ mt: 2 }}>
      {st === "on_hold" && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <b>CMTR on hold.</b> {heat.cmtr_review_reason} ({heat.cmtr_reviewed_by_name}{heat.cmtr_reviewed_by_position ? `, ${heat.cmtr_reviewed_by_position}` : ""}, {utc(heat.cmtr_reviewed_at)})
        </Alert>
      )}
      {st === "rejected" && (
        <Alert severity="error" sx={{ mb: 1 }}>
          <b>CMTR rejected{current?.ncr_number ? ` (NCR ${current.ncr_number})` : ""}.</b> {heat.cmtr_review_reason} ({heat.cmtr_reviewed_by_name}{heat.cmtr_reviewed_by_position ? `, ${heat.cmtr_reviewed_by_position}` : ""}, {utc(heat.cmtr_reviewed_at)}).
          {" "}This heat stays in quarantine. Upload a replacement CMTR to review again.
        </Alert>
      )}
      {msg && <Alert severity={msg.severity} sx={{ mb: 1 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      {canDecide && (
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Button variant="outlined" color="warning" onClick={() => open("on_hold")}>Put CMTR on Hold</Button>
          <Button variant="outlined" color="error" onClick={() => open("rejected")}>Reject CMTR</Button>
        </Stack>
      )}
      {copies && copies.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>CMTR review copies (stamped)</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Each review decision files a stamped copy. The original CMTR file is never changed. A copy opens only while it matches its registered SHA-256.
          </Typography>
          <SmartTable tableId="cmtr-reviews" lockedColumns={["#", "Result", "State"]} size="small">
            <TableHead>
              <TableRow><TableCell>#</TableCell><TableCell>Result</TableCell><TableCell>Reviewed by</TableCell><TableCell>Reviewed</TableCell><TableCell>State</TableCell><TableCell /></TableRow>
            </TableHead>
            <TableBody>
              {copies.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.number}</TableCell>
                  <TableCell><Typography variant="body2" color={`${COLOR[c.outcome]}.main`} fontWeight={700}>{LABEL[c.outcome]}{c.ncr_number ? ` (NCR ${c.ncr_number})` : ""}</Typography>
                    {c.reason && <Typography variant="caption" color="text.secondary">{c.reason}</Typography>}</TableCell>
                  <TableCell>{c.reviewer_name}{c.reviewer_position ? `, ${c.reviewer_position}` : ""}</TableCell>
                  <TableCell>{utc(c.reviewed_at)}</TableCell>
                  <TableCell>{c.current ? "Current" : <Tooltip title={c.superseded_reason || ""}><span>Superseded</span></Tooltip>}
                    {!Number(c.pages_reproduced) && <Typography variant="caption" color="warning.main" sx={{ display: "block" }}>Original pages not reproduced</Typography>}</TableCell>
                  <TableCell><Button size="small" disabled={!!opening} onClick={() => openCopy(c)}>{opening === c.id ? "Opening…" : "Open"}</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </SmartTable>
        </>
      )}
      <Dialog open={!!dialog} onClose={() => !busy && setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog === "rejected" ? "Reject CMTR" : "Put CMTR on Hold"}: heat {heat?.heatNumber}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {dialog === "rejected"
              ? "The CMTR does not meet the purchase order or the material specification. The heat stays in quarantine; only a replacement CMTR can be reviewed again. A stamped REJECTED copy is filed, signed by you."
              : "The review cannot be completed yet (for example, a supplement is awaited). The heat stays in quarantine. A stamped ON HOLD copy is filed, signed by you."}
            {heat?.cmtrVerified ? " This CMTR is marked compliant: this decision withdraws that mark, and the heat cannot be released until it is reviewed again." : ""}
          </Typography>
          <TextField label="Reason (written so an auditor can follow it)" fullWidth multiline minRows={3} value={reason}
            onChange={(e) => setReason(e.target.value)} error={reason.length > 0 && !reasonOk}
            helperText={reason.length > 0 && !reasonOk ? "5 to 1000 characters" : " "} />
          {dialog === "rejected" && (
            ncrs.length ? (
              <TextField select label="NCR raised for this heat" fullWidth value={ncrId} onChange={(e) => setNcrId(e.target.value)} sx={{ mt: 1 }}>
                {ncrs.map((n) => <MenuItem key={n.id} value={n.id}>{n.ncr_number} ({n.status}): {String(n.description || "").slice(0, 60)}</MenuItem>)}
              </TextField>
            ) : (
              <Alert severity="info" sx={{ mt: 1 }}>No open NCR names heat {heat?.heatNumber}. Raise one on QA / NCR with this heat number, then reject.</Alert>
            )
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Document review only. The stamp does not certify the material or witness the mill's tests.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color={dialog === "rejected" ? "error" : "warning"} onClick={submit}
            disabled={busy || !reasonOk || (dialog === "rejected" && !ncrId)}>
            {busy ? "Recording…" : dialog === "rejected" ? "Reject and file stamped copy" : "Hold and file stamped copy"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
